
import { useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DialogClose } from "@/components/ui/dialog";
import { addToWaitlist } from "@/services/reservationService";

interface WaitlistFormProps {
  checkIn?: Date;
  checkOut?: Date;
}

const WaitlistForm = ({ checkIn, checkOut }: WaitlistFormProps) => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    guests: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await addToWaitlist({
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        guests: parseInt(formData.guests),
        message: formData.message || null,
        preferred_check_in: checkIn ? checkIn.toISOString().split('T')[0] : null,
        preferred_check_out: checkOut ? checkOut.toISOString().split('T')[0] : null
      });

      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        guests: "",
        message: "",
      });

      toast.success(
        "Votre demande a bien été ajoutée à notre liste d'attente. Nous vous contacterons en priorité si ces dates se libèrent."
      );
    } catch (error: any) {
      toast.error(
        error.message || "Une erreur est survenue lors de l'envoi du formulaire. Veuillez réessayer."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDateRange = () => {
    if (checkIn && checkOut) {
      return `${format(checkIn, "PP", { locale: fr })} → ${format(checkOut, "PP", { locale: fr })}`;
    }
    return "Dates non spécifiées";
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {(checkIn && checkOut) && (
        <div className="p-3 bg-villa-sand/50 rounded-md text-center mb-4">
          <p className="font-medium">
            Liste d'attente pour : <span className="text-villa-blue">{formatDateRange()}</span>
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label htmlFor="first_name" className="text-sm font-medium">
            Prénom
          </label>
          <Input
            id="first_name"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            required
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="last_name" className="text-sm font-medium">
            Nom
          </label>
          <Input
            id="last_name"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            required
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium">
            Email
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="phone" className="text-sm font-medium">
            Téléphone
          </label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="guests" className="text-sm font-medium">
          Nombre de personnes
        </label>
        <Input
          id="guests"
          name="guests"
          type="number"
          min="1"
          max="8"
          value={formData.guests}
          onChange={handleChange}
          required
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="message" className="text-sm font-medium">
          Message (optionnel)
        </label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={4}
        />
      </div>

      <div className="flex justify-end space-x-4 pt-2">
        <DialogClose asChild>
          <Button type="button" variant="outline">
            Annuler
          </Button>
        </DialogClose>
        <Button
          type="submit"
          className="bg-villa-turquoise hover:bg-opacity-90"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Envoi en cours..." : "Rejoindre la liste d'attente"}
        </Button>
      </div>
    </form>
  );
};

export default WaitlistForm;
